import { useState, useEffect } from "react";

interface MaintenancePageProps {
  logoSrc: string;
  logoAlt: string;
  accentColor: string;
  glowRgb: string;
  bgFrom: string;
  whatsappLink?: string;
  telegramLink?: string;
}

export default function MaintenancePage({
  logoSrc,
  logoAlt,
  accentColor,
  glowRgb,
  bgFrom,
  whatsappLink,
  telegramLink,
}: MaintenancePageProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timeout);
  }, []);

  const hasContact = whatsappLink || telegramLink;

  return (
    <div className="min-h-screen flex flex-col bg-[#0D0D0D] font-['Inter',sans-serif] relative overflow-hidden">

      {/* Background glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full blur-3xl pointer-events-none"
        style={{ background: `rgba(${glowRgb},0.1)` }}
      ></div>
      <div
        className="absolute top-0 left-0 w-full h-full pointer-events-none"
        style={{ background: `radial-gradient(ellipse at center, ${bgFrom} 0%, #0D0D0D 70%)` }}
      ></div>

      {/* Maintenance Notice */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-16">
        <div
          className={`w-full max-w-2xl transition-all duration-700 ease-out ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Card */}
          <div
            className="rounded-3xl px-8 py-14 sm:px-16 sm:py-16 flex flex-col items-center text-center relative overflow-hidden"
            style={{
              background: `linear-gradient(160deg, ${bgFrom} 0%, #161410 50%, #111010 100%)`,
              border: `1px solid ${accentColor}`,
              boxShadow: `0 0 60px rgba(${glowRgb},0.12), inset 0 1px 0 rgba(${glowRgb},0.2)`,
            }}
          >
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-12 h-12 rounded-tl-3xl pointer-events-none" style={{ borderTop: `2px solid ${accentColor}99`, borderLeft: `2px solid ${accentColor}99` }}></div>
            <div className="absolute top-0 right-0 w-12 h-12 rounded-tr-3xl pointer-events-none" style={{ borderTop: `2px solid ${accentColor}99`, borderRight: `2px solid ${accentColor}99` }}></div>
            <div className="absolute bottom-0 left-0 w-12 h-12 rounded-bl-3xl pointer-events-none" style={{ borderBottom: `2px solid ${accentColor}99`, borderLeft: `2px solid ${accentColor}99` }}></div>
            <div className="absolute bottom-0 right-0 w-12 h-12 rounded-br-3xl pointer-events-none" style={{ borderBottom: `2px solid ${accentColor}99`, borderRight: `2px solid ${accentColor}99` }}></div>

            {/* Logo */}
            <div
              className="w-48 h-16 overflow-hidden mb-8 flex-shrink-0 flex items-center justify-center"
              style={{ filter: `drop-shadow(0 0 16px rgba(${glowRgb},0.5))` }}
            >
              <img src={logoSrc} alt={logoAlt} className="w-full h-full object-contain" />
            </div>

            {/* Animated Icon */}
            <div className="relative w-24 h-24 flex items-center justify-center mb-10">
              <div className="absolute inset-0 rounded-full" style={{ background: `rgba(${glowRgb},0.1)` }}></div>
              <div className="absolute inset-0 rounded-full animate-ping" style={{ background: `rgba(${glowRgb},0.05)` }}></div>
              <div className="relative z-10 w-16 h-16 flex items-center justify-center">
                <i className="ri-tools-fill text-4xl" style={{ color: accentColor, animation: "spin 8s linear infinite" }}></i>
              </div>
              <div className="absolute -top-1 -right-1 w-7 h-7 flex items-center justify-center rounded-full" style={{ background: accentColor }}>
                <i className="ri-settings-3-fill text-[#0D0D0D] text-sm" style={{ animation: "spin 5s linear infinite reverse" }}></i>
              </div>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight tracking-tight mb-4">
              We&apos;ll Be Back<br />
              <span style={{ color: accentColor }}>Shortly</span>
            </h1>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-16" style={{ background: `linear-gradient(to right, transparent, ${accentColor})` }}></div>
              <i className="ri-star-fill text-xs" style={{ color: accentColor }}></i>
              <div className="h-px w-16" style={{ background: `linear-gradient(to left, transparent, ${accentColor})` }}></div>
            </div>

            {/* Subheadline */}
            <p className="text-lg font-medium mb-4" style={{ color: accentColor }}>
              Our website is currently undergoing scheduled maintenance.
            </p>

            {/* Description */}
            <p className="text-base leading-relaxed max-w-lg text-gray-400">
              We&apos;re working hard to improve your experience and bring you something even better. We apologize for any inconvenience and appreciate your patience.
            </p>

            {/* Contact Buttons */}
            {hasContact && (
              <div className="mt-8 w-full">
                {/* Divider */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="h-px flex-1" style={{ background: `rgba(${glowRgb},0.15)` }}></div>
                  <span className="text-xs text-gray-600 uppercase tracking-widest whitespace-nowrap">Contact Us</span>
                  <div className="h-px flex-1" style={{ background: `rgba(${glowRgb},0.15)` }}></div>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  {whatsappLink && (
                    <a
                      href={whatsappLink}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="group flex items-center justify-center gap-2.5 w-full sm:w-auto px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200"
                      style={{
                        background: "rgba(37,211,102,0.12)",
                        color: "#25D366",
                        border: "1px solid rgba(37,211,102,0.3)",
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.background = "rgba(37,211,102,0.22)";
                        (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(37,211,102,0.6)";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.background = "rgba(37,211,102,0.12)";
                        (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(37,211,102,0.3)";
                      }}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-whatsapp-line text-base"></i>
                      </div>
                      Chat on WhatsApp
                    </a>
                  )}

                  {telegramLink && (
                    <a
                      href={telegramLink}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="group flex items-center justify-center gap-2.5 w-full sm:w-auto px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200"
                      style={{
                        background: "rgba(0,136,204,0.12)",
                        color: "#2AABEE",
                        border: "1px solid rgba(0,136,204,0.3)",
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.background = "rgba(0,136,204,0.22)";
                        (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(0,136,204,0.6)";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.background = "rgba(0,136,204,0.12)";
                        (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(0,136,204,0.3)";
                      }}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-telegram-line text-base"></i>
                      </div>
                      Message on Telegram
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Bottom tag */}
          <div className="mt-6 flex items-center justify-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accentColor }}></span>
            <p className="text-xs text-gray-500 tracking-widest uppercase">Maintenance in progress</p>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accentColor }}></span>
          </div>
        </div>
      </main>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
      `}</style>
    </div>
  );
}
